import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { useCall, type CallKind, type UseCallOptions, type UseCallResult, type RemotePeer } from '@/lib/use-call'

// Lightweight metadata about the call that we keep so the floating
// mini-window can render display-name / host info without a server
// round-trip when the user navigates away from the live page.
export type ActiveCallMeta = {
  roomKey: string
  kind: CallKind
  title: string
  host: { id: string; handle: string; display_name: string; avatar_url: string | null }
}

type PersistentCallApi = {
  active: ActiveCallMeta | null
  call: UseCallResult
  isMinimized: boolean
  minimize: () => void
  maximize: () => void
  attach: (meta: ActiveCallMeta, opts: UseCallOptions) => void
  detach: () => void   // ends the call entirely
  remoteByUserId: (userId: string) => RemotePeer | null
}

const PersistentCallContext = createContext<PersistentCallApi | null>(null)

// Sits at the React root (above the Inertia App). Holds the active call's
// state so it survives Inertia page navigation. When `isMinimized=true`,
// the global floater renders the call in a corner and the user can browse
// the rest of the app freely.
export function PersistentCallProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveCallMeta | null>(null)
  const [opts, setOpts] = useState<UseCallOptions | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)

  // useCall handles the null case as a no-op (no peer, no media).
  const call = useCall(active?.roomKey ?? null, opts)

  const attach = useCallback((meta: ActiveCallMeta, nextOpts: UseCallOptions) => {
    setActive((prev) => (prev?.roomKey === meta.roomKey ? prev : meta))
    // Dedup must include `isHost`. Otherwise: a viewer who joined a live,
    // got promoted, then left and rejoined as cohost — when Stage.tsx
    // calls attach() again with the now-true isHost, we'd silently keep
    // the stale opts where isHost=false, and useCall's mediaForKind
    // would skip the initial publish ("Request to join live" UI even
    // though the user is server-side a cohost).
    setOpts((prev) => (
      prev?.kind === nextOpts.kind &&
      prev.myUserId === nextOpts.myUserId &&
      prev.isHost === nextOpts.isHost
        ? prev
        : nextOpts
    ))
    setIsMinimized(false)
  }, [])

  const detach = useCallback(() => {
    void call.end().catch(() => {})
    setActive(null)
    setOpts(null)
    setIsMinimized(false)
  }, [call])

  const minimize = useCallback(() => setIsMinimized(true), [])
  const maximize = useCallback(() => setIsMinimized(false), [])

  // When the call transitions to `ended` (host hung up, or the cable
  // broadcast `ended` arrived), drop the active meta so the floater
  // disappears instead of showing a frozen tile. Pages handle their own
  // navigation on `ended` via UseCallOptions.onEvent; this ensures the
  // global UI doesn't outlive the call regardless of where the user is.
  useEffect(() => {
    if (active && call.state === 'ended') {
      setActive(null)
      setOpts(null)
      setIsMinimized(false)
    }
  }, [active, call.state])

  const remoteByUserId = useCallback(
    (userId: string) => call.remotePeers.find((p) => p.userId === userId) ?? null,
    [call.remotePeers]
  )

  return (
    <PersistentCallContext.Provider value={{ active, call, isMinimized, minimize, maximize, attach, detach, remoteByUserId }}>
      {children}
    </PersistentCallContext.Provider>
  )
}

export function usePersistentCall(): PersistentCallApi {
  const ctx = useContext(PersistentCallContext)
  if (!ctx) {
    throw new Error('usePersistentCall must be used inside <PersistentCallProvider>')
  }
  return ctx
}
